// System directives

"use client";

// System dependencies

import Link from "next/link";
import React from "react";
import { useTheme } from "next-themes";

// Application dependencies

import { Button } from "@/components/ui/button";
import { FormErrorFields, FormErrorRedux } from "@/interfaces/formError.interfaces";
import { formErrorActive } from "@/redux/formError/formError.slices";
import { manageFormSpinner } from "@/lib/functions";
import { useAppDispatch } from "@/redux/store.hooks";

const FormError: React.FC<FormErrorRedux> = ({
  buttonCaption = "",
  buttonRoute = "",
  code = "404",
  isActive,
  message = "This page could not be found",
  showClose = false,
}) => {
  // Application state

  const { theme } = useTheme();

  // Redux state

  const dispatch = useAppDispatch();

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  return !isActive ? null : (
    <div className="fixed inset-0 z-50 flex h-screen flex-col items-center justify-center bg-white text-center font-sans text-black dark:bg-slate-900 dark:text-zinc-100">
      {/* Display the error message */}

      <div className="flex flex-col items-center sm:flex-row sm:justify-center sm:space-x-4">
        {/* Display code */}

        <h1 className="text-2xl font-medium">{code}</h1>

        {/* Vertical rule and message for >sm */}

        <div className="hidden sm:flex sm:items-center sm:space-x-4">
          <span
            className="h-10 border-r"
            style={{
              borderRightColor: theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.3)",
            }}
          ></span>
          <h2 className="text-lg font-light">{message}</h2>
        </div>

        {/* Message for <=sm */}

        <h2 className="mt-2 text-lg font-light sm:hidden">{message}</h2>
      </div>

      {/* Display the button if active */}

      {showClose ? (
        <div className="mt-10 flex">
          <Button
            className="min-w-[200px] hover:bg-zinc-500 dark:hover:bg-zinc-300"
            onClick={() => dispatch(formErrorActive({ [FormErrorFields.IsActive]: false }))}
          >
            Close
          </Button>
        </div>
      ) : (
        buttonCaption &&
        buttonRoute && (
          <div className="mt-10 flex" onClick={() => manageFormSpinner(`loading ${buttonCaption} page - please wait`)}>
            <Link href={buttonRoute}>
              <Button
                className="min-w-[200px] hover:bg-zinc-500 dark:hover:bg-zinc-300"
                onClick={() => dispatch(formErrorActive({ [FormErrorFields.IsActive]: false }))}
              >
                load {buttonCaption}
              </Button>
            </Link>
          </div>
        )
      )}
    </div>
  );
};

export default FormError;
